/**
 * 2D math on the ground plane. The simulation lives on the XZ plane so that sim
 * coordinates map 1:1 onto three.js world coordinates (y is height, render-only).
 *
 * Facing convention: an angle `a` points along (sin a, cos a), which matches
 * three.js `object.rotation.y = a` for models whose forward axis is +Z (glTF).
 */

export interface V2 {
  x: number;
  z: number;
}

export const TAU = Math.PI * 2;

export const v2 = (x = 0, z = 0): V2 => ({ x, z });
export const copy = (a: V2): V2 => ({ x: a.x, z: a.z });
export const set = (out: V2, x: number, z: number): V2 => {
  out.x = x;
  out.z = z;
  return out;
};
export const add = (a: V2, b: V2): V2 => ({ x: a.x + b.x, z: a.z + b.z });
export const sub = (a: V2, b: V2): V2 => ({ x: a.x - b.x, z: a.z - b.z });
export const scale = (a: V2, s: number): V2 => ({ x: a.x * s, z: a.z * s });
export const dot = (a: V2, b: V2): number => a.x * b.x + a.z * b.z;
/** 2D perp-dot product: a.x * b.z - a.z * b.x. */
export const cross = (a: V2, b: V2): number => a.x * b.z - a.z * b.x;
export const len2 = (a: V2): number => a.x * a.x + a.z * a.z;
export const len = (a: V2): number => Math.hypot(a.x, a.z);
export const dist2 = (a: V2, b: V2): number => {
  const dx = a.x - b.x;
  const dz = a.z - b.z;
  return dx * dx + dz * dz;
};
export const dist = (a: V2, b: V2): number => Math.hypot(a.x - b.x, a.z - b.z);

export const normalize = (a: V2): V2 => {
  const l = len(a);
  return l > 1e-9 ? { x: a.x / l, z: a.z / l } : { x: 0, z: 0 };
};

export const addScaled = (out: V2, a: V2, s: number): V2 => {
  out.x += a.x * s;
  out.z += a.z * s;
  return out;
};

export const lerpV = (a: V2, b: V2, t: number): V2 => ({
  x: a.x + (b.x - a.x) * t,
  z: a.z + (b.z - a.z) * t,
});

export const dirFromAngle = (a: number): V2 => ({ x: Math.sin(a), z: Math.cos(a) });
export const angleOf = (d: V2): number => Math.atan2(d.x, d.z);
export const angleTo = (from: V2, to: V2): number => Math.atan2(to.x - from.x, to.z - from.z);

/** Rotate a vector by `a` radians using the same convention as `dirFromAngle`. */
export const rotate = (d: V2, a: number): V2 => {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: d.x * c + d.z * s, z: -d.x * s + d.z * c };
};

/** Wrap to (-PI, PI]. */
export const wrapAngle = (a: number): number => {
  let r = a % TAU;
  if (r <= -Math.PI) r += TAU;
  else if (r > Math.PI) r -= TAU;
  return r;
};

/** Signed shortest difference b - a in (-PI, PI]. */
export const angleDiff = (a: number, b: number): number => wrapAngle(b - a);

export const rotateTowards = (from: number, to: number, maxStep: number): number => {
  const d = angleDiff(from, to);
  if (Math.abs(d) <= maxStep) return wrapAngle(to);
  return wrapAngle(from + Math.sign(d) * maxStep);
};

export const lerpAngle = (a: number, b: number, t: number): number => wrapAngle(a + angleDiff(a, b) * t);

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number): number => clamp(v, 0, 1);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const invLerp = (a: number, b: number, v: number): number => (b === a ? 0 : (v - a) / (b - a));
export const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = clamp01((x - e0) / (e1 - e0));
  return t * t * (3 - 2 * t);
};

/** Frame-rate independent exponential smoothing factor for `lerp(current, target, damp(lambda, dt))`. */
export const damp = (lambda: number, dt: number): number => 1 - Math.exp(-lambda * dt);

export const approach = (v: number, target: number, step: number): number =>
  v < target ? Math.min(v + step, target) : Math.max(v - step, target);

/** Closest point on segment ab to p. */
export const closestOnSegment = (p: V2, a: V2, b: V2): V2 => {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const l2 = abx * abx + abz * abz;
  if (l2 < 1e-12) return copy(a);
  const t = clamp01(((p.x - a.x) * abx + (p.z - a.z) * abz) / l2);
  return { x: a.x + abx * t, z: a.z + abz * t };
};

export const roundTo = (v: number, step: number): number => Math.round(v / step) * step;
